import React, { useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaEnvelope, FaLock } from 'react-icons/fa';
import { PopupContext } from '../App';
import AuthLayout, { AuthFields, AuthFooter, AuthSubmitRow } from '../features/auth/components/AuthLayout';
import authStyles from '../features/auth/components/AuthLayout.module.css';
import Button from '../shared/ui/Button';
import Input from '../shared/ui/Input';
import { login } from '../services/authService';
import axios from 'axios';
import { API_URL } from '../config/env';
import { safeReturnPath } from '../utils/authRedirect';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [accountLocked, setAccountLocked] = useState(false);
  const showPopup = useContext(PopupContext);

  const goAfterLogin = () => {
    const ret = safeReturnPath(searchParams.get('returnUrl'));
    navigate(ret || '/dashboard', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (accountLocked) return;
    setLoading(true);
    try {
      await login(email, password);
      showPopup('Login successful!', 'success');
      goAfterLogin();
    } catch (err) {
      const code = err.response?.data?.code;
      const status = err.response?.status;
      if (code === 'ACCOUNT_LOCKED' || status === 423) {
        setAccountLocked(true);
        showPopup('Too many attempts — use “Forgot password” below.', 'error');
      } else {
        showPopup('Invalid email or password', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await axios.post(`${API_URL}/api/email/forgot-password`, { receiver: email });
      showPopup('If this email exists, a reset link was sent.', 'success');
    } catch {
      showPopup('Failed to send reset email', 'error');
    } finally {
      setResetLoading(false);
    }
  };

  const lockedSubtitle =
    'This account had too many failed sign-ins. You can only set a new password using the reset link we can send to your email.';

  return (
    <AuthLayout
      title={showReset || accountLocked ? 'Reset password' : 'Sign in'}
      subtitle={
        accountLocked
          ? lockedSubtitle
          : showReset
            ? 'Enter your email and we will send you a link to choose a new password.'
            : 'Welcome back to Voucher Portal.'
      }
      credit={
        <p>
          Powered by{' '}
          <a href="https://clearonthecloud.com" target="_blank" rel="noopener noreferrer">
            Clear on the Cloud
          </a>
        </p>
      }
    >
      {!showReset && !accountLocked ? (
        <form onSubmit={handleSubmit} noValidate>
          <AuthFields>
            <Input
              type="email"
              label="Email"
              placeholder="you@school.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              iconLeft={<FaEnvelope />}
            />
            <Input
              type="password"
              label="Password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              iconLeft={<FaLock />}
            />
          </AuthFields>
          <AuthSubmitRow>
            <Button type="submit" size="lg" loading={loading}>
              Sign in
            </Button>
          </AuthSubmitRow>
          <AuthFooter>
            <button type="button" className={authStyles.textButton} onClick={() => setShowReset(true)}>
              Forgot password?
            </button>
          </AuthFooter>
        </form>
      ) : (
        <form onSubmit={handleReset} noValidate>
          <AuthFields>
            <Input
              type="email"
              label="Email"
              placeholder="you@school.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              iconLeft={<FaEnvelope />}
            />
          </AuthFields>
          <AuthSubmitRow>
            <Button type="submit" size="lg" loading={resetLoading}>
              Send reset link
            </Button>
          </AuthSubmitRow>
          {!accountLocked ? (
            <AuthFooter>
              <button type="button" className={authStyles.textButton} onClick={() => setShowReset(false)}>
                Back to sign in
              </button>
            </AuthFooter>
          ) : null}
        </form>
      )}
    </AuthLayout>
  );
}
